import { useCallback, useEffect, useState } from "react";
import type { ApiKeyInfo, ApiScope, RevisionInfo } from "@deckloom/core/protocol";
import { api } from "../api";

const SCOPES: { scope: ApiScope; label: string }[] = [
  { scope: "present", label: "present (next, previous, go to)" },
  { scope: "draw", label: "draw (drawings and images on the pages)" },
  { scope: "edit", label: "edit (the slides, pictures, notes, revisions)" },
];

/** The secrets of the keys made in this browser, by key id (the server keeps only a hash): the copy button needs them. */
function loadSecrets(deckId: string): Record<string, string> {
  try { return (JSON.parse(localStorage.getItem(`deckloom:keys:${deckId}`) ?? "{}") as Record<string, string>) ?? {}; } catch { return {}; }
}
function saveSecrets(deckId: string, secrets: Record<string, string>): void {
  try { localStorage.setItem(`deckloom:keys:${deckId}`, JSON.stringify(secrets)); } catch { /* storage blocked: the copy button only lives this long */ }
}

/**
 * The operator's panel in solo mode: the deck's API for scripts and AI agents. Make, list and revoke API keys;
 * a key made here has a copy button with everything an agent needs (the deck, the key, the docs and how to
 * use them). The revisions of the slides: Apply shows one in the room, Go back makes one the working copy.
 */
export function ApiKeys({ deckId, token, onChanged }: { deckId: string; token: string; onChanged?: () => void }) {
  const [keys, setKeys] = useState<ApiKeyInfo[] | null>(null);
  const [secrets, setSecrets] = useState<Record<string, string>>(() => loadSecrets(deckId));
  const [name, setName] = useState("");
  const [scopes, setScopes] = useState<ApiScope[]>(["present", "draw", "edit"]);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState<string | null>(null);
  const [revisions, setRevisions] = useState<RevisionInfo[] | null>(null);
  const [busy, setBusy] = useState<string | null>(null);

  const load = useCallback(() => {
    api.apiKeys(deckId, token).then(setKeys).catch((e) => setError((e as Error).message));
    api.revisions(deckId).then(setRevisions).catch(() => setRevisions([]));
  }, [deckId, token]);
  useEffect(load, [load]);

  const create = async () => {
    setError(null);
    try {
      const made = await api.createApiKey(deckId, token, name.trim(), scopes);
      const next = { ...secrets, [made.id]: made.key };
      setSecrets(next);
      saveSecrets(deckId, next);
      setName("");
      load();
    } catch (e) {
      setError((e as Error).message);
    }
  };

  const revoke = async (k: ApiKeyInfo) => {
    if (!window.confirm(`Revoke "${k.name || k.id}"? Apps using it stop working.`)) return;
    try {
      await api.revokeApiKey(deckId, token, k.id);
      const { [k.id]: _gone, ...rest } = secrets;
      setSecrets(rest);
      saveSecrets(deckId, rest);
      load();
    } catch (e) {
      setError((e as Error).message);
    }
  };

  const base = `${location.origin}/api/decks/${deckId}`;
  const docs = `${location.origin}/llms.txt`;
  /** everything an agent needs, in one paste */
  const brief = (key: string) => [
    `Deck: ${base}`,
    `Key: ${key}`,
    `Docs: ${docs}`,
    "",
    "How to use: read the docs first. Send the key as `Authorization: Bearer <key>` on every call that changes something;",
    "reading needs no key. Edits (POST .../edits, PUT .../pages/<page>/notes) change the working copy and save a",
    "revision; nothing shows to the audience until a revision is applied (POST .../revisions/<n>/apply). Drawings",
    "(POST .../pages/<page>/drawings) show live at once. Read deck.json for page and element ids before editing.",
  ].join("\n");
  const copy = async (k: ApiKeyInfo) => {
    const key = secrets[k.id];
    if (!key) return;
    await navigator.clipboard.writeText(brief(key)).catch(() => undefined);
    setCopied(k.id);
    setTimeout(() => setCopied((c) => (c === k.id ? null : c)), 2000);
  };

  const act = async (what: "apply" | "restore", r: RevisionInfo) => {
    const ask = what === "apply"
      ? `Show revision ${r.revision} (${r.summary}) to everyone? Open players switch to it at once.`
      : `Make revision ${r.revision} (${r.summary}) the working copy? The slides as they are now stay in the history.`;
    if (!window.confirm(ask)) return;
    setBusy(`${what}:${r.revision}`);
    setError(null);
    try {
      if (what === "apply") await api.applyRevision(deckId, token, r.revision);
      else await api.restore(deckId, token, r.revision);
      load();
      onChanged?.();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(null);
    }
  };
  const when = (iso: string) => new Date(iso).toLocaleString();

  return (
    <aside className="notes api-keys">
      <h3>API</h3>
      <p className="hint">
        Scripts, AI agents and other apps change this deck over HTTP: the slides, the pictures, the notes, the drawings, the page shown.
        Make a key, then Copy hands your AI the deck, the key, the docs (<a href={docs} target="_blank" rel="noopener">{docs}</a>) and how to use them.
      </p>

      <div className="new-key">
        <input placeholder="name, e.g. my agent" value={name} maxLength={100} onChange={(e) => setName(e.target.value)} />
        {SCOPES.map(({ scope, label }) => (
          <label key={scope}>
            <input
              type="checkbox"
              checked={scopes.includes(scope)}
              onChange={(e) => setScopes((s) => (e.target.checked ? [...s, scope] : s.filter((x) => x !== scope)))}
            />{" "}
            {label}
          </label>
        ))}
        <button onClick={() => void create()} disabled={scopes.length === 0}>Create key</button>
      </div>

      {error && <p className="error">{error}</p>}

      <ul className="key-list">
        {keys?.length === 0 && <li className="hint">No keys yet.</li>}
        {keys?.map((k) => (
          <li key={k.id}>
            <span>
              <b>{k.name || k.id}</b> · {k.scopes.join(", ")}
              <br />
              <small>{k.lastUsedAt ? `used ${when(k.lastUsedAt)}` : "never used"}{secrets[k.id] ? "" : " · made in another browser, so its key cannot be copied here"}</small>
            </span>
            <span className="row">
              {secrets[k.id] && <button className="primary" onClick={() => void copy(k)} title="copy the deck, the key, the docs and how to use them, for your AI">{copied === k.id ? "Copied" : "Copy"}</button>}
              <button onClick={() => void revoke(k)}>Revoke</button>
            </span>
          </li>
        ))}
      </ul>

      <h3>History</h3>
      <p className="hint">Every edit saves the slides as a revision (the last 20 are kept, and the conversion). Nothing shows to the audience until a revision is applied. Go back makes an older one the working copy again.</p>
      <ul className="key-list">
        {revisions?.length === 0 && <li className="hint">Nothing saved yet.</li>}
        {revisions?.map((r) => (
          <li key={r.revision}>
            <span>
              <b>{r.revision}</b> · {r.summary}
              <br />
              <small>{when(r.at)} · {r.by}{r.live ? " · live" : ""}{r.head ? " · working copy" : ""}</small>
            </span>
            <span className="row">
              {!r.live && <button className="primary" disabled={busy !== null} onClick={() => void act("apply", r)}>{busy === `apply:${r.revision}` ? "Applying…" : "Apply"}</button>}
              {!r.head && <button disabled={busy !== null} onClick={() => void act("restore", r)}>{busy === `restore:${r.revision}` ? "Going back…" : "Go back"}</button>}
            </span>
          </li>
        ))}
      </ul>
    </aside>
  );
}
